<template>
  <div class="top-header">
    <div class="header-box">
      <!-- 左侧用户信息区域 -->
      <div class="header-left">
        <span class="welcome-text">您好，欢迎 {{ username }} 来到{{ siteName }}物流平台</span>
      </div>

      <!-- 右侧定位信息区域 -->
      <div class="header-right">
        <span class="location-icon">📍</span>
        <span class="location-text" @click="showLocationDialog">{{ location || '请选择所在区域' }}</span>
      </div>
    </div>

    <!-- 地区选择弹窗 -->
    <el-dialog
      v-model="locationDialogVisible"
      title="选择地区"
      width="500px"
      :before-close="handleLocationDialogClose"
    >
      <el-form :model="locationForm" label-width="80px">
        <el-form-item label="地区">
          <!-- 将搜索框和下拉框合并 -->
          <el-select
            v-model="locationForm.area"
            placeholder="请选择地区"
            style="width: 100%"
            filterable
            remote
            :remote-method="handleSearch"
            :loading="searchLoading"
            clearable
            :no-data-text="areaOptions.length === 0 ? '加载地区失败，请重试' : '暂无该地区选择'"
            @focus="retryFetchAreaData"
          >
            <el-option
              v-for="(item, index) in filteredAreaOptions"
              :key="index"
              :label="`${item.nameProv}${item.nameCity}`"
              :value="index"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="locationDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmLocation" class="confirm-button">确定</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { getSiteHostInfo, getQueryProvCity } from '@/apis/modules/login/login.js';
import { getHost } from '@/utils/domain.js';

export default {
  name: 'TopHeader',
  data() {
    return {
      location: '', // 定位信息
      username: '游客',
      siteName: '助力通', // 默认站点
      locationDialogVisible: false,
      locationForm: {
        area: '' // 地区选择
      },
      areaOptions: [],
      filteredAreaOptions: [],
      searchLoading: false,
      areaDataLoading: false // 标记地区数据加载状态
    }
  },
  watch: {
    // 监听路由变化来触发用户名更新
    $route: {
      handler() {
        this.updateUsername();
        this.checkLocationFromUrl();
      },
      immediate: true
    },
    // 监听地区数据变化，重新检查URL参数
    areaOptions: {
      handler() {
        this.checkLocationFromUrl();
      },
      deep: true
    },
    // 监听哈希变化
    '$route.hash': {
      handler() {
        this.checkLocationFromUrl();
      },
      immediate: true
    }
  },
  mounted() {
    this.updateUsername();
    window.addEventListener('storage', this.handleStorageChange)
    window.addEventListener('userLoginChange', this.handleUserLoginChange)
    window.addEventListener('userLogoutChange', this.handleUserLoginChange)
    window.addEventListener('hashchange', this.handleHashChange) // 监听哈希变化
    this.fetchSiteName();
    this.fetchLocationData(); // 首次加载
    this.checkLocationFromUrl();
  },
  beforeUnmount() {
    window.removeEventListener('storage', this.handleStorageChange)
    window.removeEventListener('userLoginChange', this.handleUserLoginChange)
    window.removeEventListener('userLogoutChange', this.handleUserLoginChange)
    window.removeEventListener('hashchange', this.handleHashChange) // 移除哈希监听
  },
  methods: {
    // 处理哈希变化
    handleHashChange() {
      this.checkLocationFromUrl();
    },

    // 打开时检查数据，无数据则重试
    showLocationDialog() {
      this.locationDialogVisible = true;
      this.locationForm.area = '';
      this.filteredAreaOptions = [...this.areaOptions];

      // 若数据为空，自动重试加载
      if (this.areaOptions.length === 0 && !this.areaDataLoading) {
        this.retryFetchAreaData();
      }
    },

    // 重试加载地区数据
    retryFetchAreaData() {
      if (!this.areaDataLoading) {
        this.fetchLocationData();
      }
    },

    // 关闭地区选择弹窗
    handleLocationDialogClose(done) {
      done();
    },

    // 获取地区数据
    async fetchLocationData() {
      this.areaDataLoading = true;
      try {
        // 若为跨域请求，可手动指定完整接口地址
        const res = await getQueryProvCity({}, {
          // 强制携带cookie（若接口需要鉴权）
          withCredentials: true,
          // 超时时间延长
          timeout: 10000
        });

        if (res && res.data) {
          this.areaOptions = res.data;
          this.filteredAreaOptions = [...res.data];
          this.checkLocationFromUrl(); // 数据加载后重新检查URL参数
        }
      } catch (error) {
        console.error('获取地区数据失败:', error);

        // 错误提示用户
        this.$message.error('地区数据加载失败，请稍后重试');

        // 不再从localStorage缓存读取
      } finally {
        this.areaDataLoading = false;
      }
    },

    // 处理搜索输入
    handleSearch(query) {
      if (!query) {
        this.filteredAreaOptions = [...this.areaOptions];
        return;
      }

      this.searchLoading = true;
      const keyword = query.toLowerCase();

      setTimeout(() => {
        this.filteredAreaOptions = this.areaOptions.filter(item => {
          return (
            item.nameProv.toLowerCase().includes(keyword) ||
            item.nameCity.toLowerCase().includes(keyword)
          );
        });
        this.searchLoading = false;
      }, 200);
    },

    // 确认选择地区
    async confirmLocation() {
      if (this.locationForm.area === '' || this.locationForm.area === null) {
        this.$message.warning('请选择地区');
        return;
      }

      const selectedIndex = this.locationForm.area;
      const selectedLocation = this.filteredAreaOptions[selectedIndex];

      if (selectedLocation && (!selectedLocation.siteHosts || selectedLocation.siteHosts.length === 0)) {
        this.location = `${selectedLocation.nameProv}·${selectedLocation.nameCity}`;
        this.locationDialogVisible = false;
        const locationParam = encodeURIComponent(this.location);
        const redirectUrl = `https://www.zhult.com/#/home?location=${locationParam}`;
        window.location.href = redirectUrl;
        return;
      }

      // 选择地区后如果有分站的话才跳转并携带地名
      let siteHost = 'www';
      let locationParam = '';
      if (selectedLocation && selectedLocation.siteHosts && selectedLocation.siteHosts.length > 0) {
        this.location = `${selectedLocation.nameProv}·${selectedLocation.nameCity}`;
        siteHost = selectedLocation.siteHosts[0];
        locationParam = encodeURIComponent(this.location);
      }

      // 不再缓存地区数据到localStorage
      // localStorage.setItem('cachedAreaData', JSON.stringify(this.areaOptions));

      this.locationDialogVisible = false;

      // 如果选择的地区没有siteHost的话直接跳到https://www.zhult.com/#/home
      let redirectUrl;
      if (siteHost === 'www') {
        redirectUrl = 'https://www.zhult.com/#/home';
      } else {
        redirectUrl = `https://${siteHost}.zhult.com/#/home?location=${locationParam}`;
      }
      window.location.href = redirectUrl;
    },

    // 哈希模式下URL参数解析
    checkLocationFromUrl() {
      // 从window.location.hash解析
      const hash = window.location.hash;
      const hashParams = new URLSearchParams(hash.split('?')[1] || '');
      const locationParam = hashParams.get('location');

      if (locationParam) {
        try {
          const decodedLocation = decodeURIComponent(locationParam);
          // 正确显示从URL传递过来的地区信息，无论是否为www分站
          this.location = decodedLocation;
        } catch (e) {
          console.error('解析location参数失败:', e);
          // 解析失败时设置为空
          this.location = '';
        }
      } else {
        // 没有URL参数时设置为空
        this.location = '';
      }
    },

    // 获取站点名称
    async fetchSiteName() {
      try {
        const host = getHost();
        const res = await getSiteHostInfo({ adHost: host });
        if (res && res.statusCode === 20000 && res.data && res.data.siteName) {
          this.siteName = res.data.siteName;
        }
      } catch (error) {
        console.error('获取站点名称失败:', error);
      }
    },

    // 获取地理位置
    getLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            // 取消本地保存机制，只在有分站的情况下设置位置
            const host = getHost();
            if (host !== 'www') {
              this.location = '中国·广东省佛山市';
            }
          },
          error => {
            console.error('定位失败:', error);
            // 取消本地保存机制，只在有分站的情况下设置位置
            const host = getHost();
            if (host !== 'www') {
              this.location = '中国·广东省佛山市';
            }
          }
        );
      } else {
        // 取消本地保存机制，只在有分站的情况下设置位置
        const host = getHost();
        if (host !== 'www') {
          this.location = '中国·广东省佛山市';
        }
      }
    },

    handleStorageChange() {
      this.updateUsername();
    },

    handleUserLoginChange() {
      this.updateUsername();
    },

    updateUsername() {
      try {
        const userInfo = localStorage.getItem('userInfo')
        if (userInfo) {
          const user = JSON.parse(userInfo)
          if (user.username) {
            this.username = user.username;
            return;
          }
        }
      } catch (e) {}
      this.username = '游客';
    }
  }
}
</script>

<style scoped lang="scss">
.top-header {
  width: 100%;
  height: 48px;
  background: #00153F;
  opacity: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
}
.header-box{
  width: 1400px;
  height: 100%;
  display: flex;
  justify-content: space-between;
}
.header-left {
  display: flex;
  align-items: center;
  color: #FFFFFF;
  font-size: 14px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #FFFFFF;
  font-size: 14px;
  cursor: pointer;
}

.location-icon {
  font-size: 16px;
}

.confirm-button {
  background-color: #001b51 !important;
  border-color: #001b51 !important;
}

@media (max-width: 1920px) {
  .top-header {
    width: 100%;
  }
}
</style>
